import {
  AvatarIcon,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  FullScreenLoading,
  Icon,
  IconButton,
  Select,
  SuggestionActionInput,
  SvgIcon,
  Text,
} from '@civilgrid/ui/components';
import { getNonNullUserName } from '@civilgrid/ui/utils';
import React, { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import { inviteMembers } from '~/api/projectsV3';
import { isValidEmail } from '~/common/miscUtils';
import Notify from '~/components2/Notify';
import rootStore from '~/store';
import { getRoles, isRoleHigherThanMine } from '../../organization/members/common';
import orgMembersStore from '../../organization/members/store';
import { AboutUserRoles, keepOpenOnSuggestionListEscape } from './components';

const EMPTY_ORG_ROLES: readonly unknown[] = [];

interface Props {
  trigger?: ReactNode;
  /** Single project: when set (and not bulk), dropdown shows only org users not in this project. */
  projectId?: string;
  /** User IDs of members on the project. When projectId is set, exclude these from dropdown. Bulk: pass [] to show all org members. */
  projectMemberUserIds?: string[];
  /** Called after project members are saved (single-project flow). */
  onSave?: () => void | Promise<void>;
  /** When provided (bulk invite), submit uses this with userIds and memberData. Dropdown shows all org users. */
  onBulkInvite?: (payload: { userIds: string[]; memberData?: { email: string; role: string }[] }) => Promise<void>;
  /** When provided with onOpenChange, dialog is controlled (e.g. from bulk actions). No trigger rendered. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** When controlled, pass to disable submit button during bulk invite request. */
  isPending?: boolean;
}

export default function InviteTeamProjectDialog({
  trigger = <Button>Invite Team</Button>,
  projectId,
  projectMemberUserIds = [],
  onSave,
  onBulkInvite,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  isPending: controlledIsPending = false,
}: Props) {
  const { currentOrganizationDetail, me, myRole } = useSnapshot(rootStore);
  const { selectedExternalMembers, selectedExternalMembersArray, isLoading } = useSnapshot(orgMembersStore);

  const currentOrgMembers = useMemo(() => {
    if (currentOrganizationDetail.isLoading) return [];
    return currentOrganizationDetail.members ?? [];
  }, [currentOrganizationDetail]);

  const orgRoles = useMemo(
    () => (currentOrganizationDetail.isLoading ? EMPTY_ORG_ROLES : currentOrganizationDetail.roles),
    [currentOrganizationDetail],
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const roles = useMemo(
    () => getRoles().filter((role) => typeof role.value === 'string' && !isRoleHigherThanMine(role.value)),
    [currentOrganizationDetail.isLoading, orgRoles, myRole],
  );

  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined && controlledOnOpenChange !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? controlledOnOpenChange : setInternalOpen;
  const [searchValue, setSearchValue] = useState('');
  const [contentEl, setContentEl] = useState<HTMLDivElement | null>(null);
  const [pendingAddUserIds, setPendingAddUserIds] = useState<string[]>([]);

  // When controlled (e.g. bulk actions), clear internal state when dialog closes so it resets on next open.
  useEffect(() => {
    if (isControlled && !open) {
      orgMembersStore.resetSelections();
      setPendingAddUserIds([]);
      setSearchValue('');
    }
  }, [isControlled, open]);

  const projectMemberIdSet = useMemo(
    () => new Set(projectMemberUserIds?.map((id) => String(id)) ?? []),
    [projectMemberUserIds],
  );

  const orgMembersNotOnProject = useMemo(
    () =>
      currentOrgMembers.filter(
        (m) =>
          !projectMemberIdSet.has(String(m.user.id)) &&
          String(m.user.id) !== String(me?.id) &&
          m.status === 'active' &&
          (m.user?.status === 'active' || m.user?.status === 'invited'),
      ),
    [currentOrgMembers, projectMemberIdSet, me?.id],
  );

  const pendingAddSet = useMemo(() => new Set(pendingAddUserIds), [pendingAddUserIds]);

  const addOrgMemberToPending = (userId: string) => {
    if (pendingAddSet.has(userId)) return;
    setPendingAddUserIds((prev) => [...prev, userId]);
  };

  // Comma-separated paste: add each valid email and keep remainder in input
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (!searchValue.includes(',')) return;
    const parts = searchValue.split(',');
    let remainder = '';
    for (const part of parts) {
      const trimmed = part.trim();
      if (trimmed && isValidEmail(trimmed)) {
        if (projectId) {
          const member = currentOrgMembers.find(
            (m) =>
              m.user?.email?.toLowerCase() === trimmed.toLowerCase() &&
              !projectMemberIdSet.has(String(m.user.id)) &&
              String(m.user.id) !== String(me?.id),
          );
          if (member) addOrgMemberToPending(String(member.user.id));
          else orgMembersStore.addExternalMember(trimmed);
        } else {
          orgMembersStore.addExternalMember(trimmed);
        }
      } else {
        remainder += (remainder ? ',' : '') + part;
      }
    }
    if (remainder !== searchValue) {
      setSearchValue(remainder.trim());
    }
  }, [searchValue, projectId, currentOrgMembers, projectMemberIdSet, me?.id]);

  const handleSubmitEmail = (email: string) => {
    if (!isValidEmail(email)) return;
    if (projectId) {
      const member = currentOrgMembers.find(
        (m) =>
          m.user?.email?.toLowerCase() === email.toLowerCase() &&
          !projectMemberIdSet.has(String(m.user.id)) &&
          String(m.user.id) !== String(me?.id),
      );
      if (member) addOrgMemberToPending(String(member.user.id));
      else orgMembersStore.addExternalMember(email);
    } else {
      orgMembersStore.addExternalMember(email);
    }
  };

  // MemberSearch-style options: org members not on project, filterable by name or email
  const suggestionOptions = useMemo(
    () =>
      orgMembersNotOnProject
        .filter((m) => !pendingAddSet.has(String(m.user.id)))
        .map((m) => {
          const userId = String(m.user.id);
          const name = getNonNullUserName(m.user.name);
          const email = m.user.email ?? '';
          return {
            value: `${name} ${email}`.trim(),
            label: (
              <div className="flex items-center gap-2 w-full min-w-0">
                <AvatarIcon user={{ name, email }} size="small" className="shrink-0" />
                <div className="flex items-center gap-2 min-w-0 flex-1 truncate">
                  <Text className="text-xs text-grey-80 truncate">{name || email}</Text>
                  <Text className="text-xs text-grey-50 truncate">{email}</Text>
                </div>
                <div className="flex items-center gap-1 shrink-0 text-primary">
                  <Icon icon="plus" size={14} />
                  <Text className="text-xs font-medium">Add</Text>
                </div>
              </div>
            ),
            onSelect: () => setPendingAddUserIds((prev) => (prev.includes(userId) ? prev : [...prev, userId])),
          };
        }),
    [orgMembersNotOnProject, pendingAddSet],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const isValidState = useMemo<boolean>(() => {
    if (!selectedExternalMembers.size) return false;

    return selectedExternalMembersArray.every(({ email, role }) => {
      if (isValidEmail(email) && role) return true;
      return false;
    });
  }, [selectedExternalMembersArray]);

  // Allow submit only when: (a) external entries exist and are all valid, or (b) no external entries and pending org adds exist, or (c) both. Invalid external entries must not allow submit even if pending exists.
  const hasAnythingToSubmit =
    (selectedExternalMembersArray.length === 0 && pendingAddUserIds.length > 0) ||
    (selectedExternalMembersArray.length > 0 && isValidState);

  const handleInviteMembers = async () => {
    if (!hasAnythingToSubmit) return;
    orgMembersStore.isLoading = true;

    try {
      if (currentOrganizationDetail.isLoading) return;
      const orgId = currentOrganizationDetail.id;
      if (!orgId) return;

      const memberData =
        selectedExternalMembersArray.length > 0 && isValidState
          ? selectedExternalMembersArray.map(({ email, role }) => ({ email, role }))
          : undefined;

      if (onBulkInvite) {
        await onBulkInvite({
          userIds: pendingAddUserIds,
          memberData,
        });
      } else {
        await inviteMembers({
          organizationId: orgId,
          projectId: projectId,
          ...(pendingAddUserIds.length > 0 && { userIds: pendingAddUserIds }),
          ...(memberData && { memberData }),
        });
        await onSave?.();
      }

      setPendingAddUserIds([]);
      orgMembersStore.resetSelections();
      Notify.info({
        message: onBulkInvite
          ? 'Team members added to selected projects!'
          : memberData && pendingAddUserIds.length > 0
            ? 'Invites sent and project team updated!'
            : pendingAddUserIds.length > 0
              ? 'Project team updated!'
              : `Invite${memberData?.length ? '(s)' : ''} sent!`,
      });
      setOpen(false);
    } catch (err) {
      console.error(err);
      Notify.error({
        message: `Error sending invite${selectedExternalMembers.size > 0 && '(s)'} !`,
      });
    } finally {
      orgMembersStore.isLoading = false;
    }
  };

  const handleUnstageAdd = (userId: string) => {
    setPendingAddUserIds((prev) => prev.filter((id) => id !== userId));
  };

  const hasPendingInput =
    searchValue.trim() !== '' || pendingAddUserIds.length > 0 || selectedExternalMembersArray.length > 0;

  if (isLoading) {
    return <FullScreenLoading />;
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (isOpen === false) {
          orgMembersStore.resetSelections();
          setPendingAddUserIds([]);
          setSearchValue('');
        }
      }}
    >
      {!isControlled && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent
        ref={setContentEl}
        data-testid="invite-team-project-dialog"
        title="Invite Team Members"
        preventDismiss={hasPendingInput}
        onEscapeKeyDown={keepOpenOnSuggestionListEscape}
        // Lets the suggestion list extend past the dialog's bottom edge instead of clipping.
        className="w-150 max-w-auto overflow-visible"
      >
        <DialogBody className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Text className="text-xs text-grey-80 font-medium">
              {projectId ? 'Add new members to this project' : 'Add members to selected projects'}
            </Text>
            <SuggestionActionInput
              options={suggestionOptions}
              value={searchValue}
              onChange={setSearchValue}
              onSubmit={handleSubmitEmail}
              customOptionLabel={(v) => (
                <div className="text-xs text-grey-90">
                  {isValidEmail(v) ? (
                    <div className="flex items-center gap-1">
                      <Icon icon="luUserPlus" />
                      <Text className="text-xs text-grey-90">Invite {v} to your organization</Text>
                    </div>
                  ) : (
                    'Please enter a valid email'
                  )}
                </div>
              )}
              container={contentEl}
              placeholder="Enter email or search org members"
            />
          </div>
          <div className="flex flex-col space-y-2">
            <Text className="text-xs">
              Use a name or email address to find users already a part of your organization.
            </Text>
            <Text className="text-xs">
              Type a person's email address to invite them to the platform, you can have as many users as you want in
              your organization.
            </Text>
          </div>
          {(projectId != null || onBulkInvite) && pendingAddUserIds.length > 0 && (
            <div className="flex flex-col gap-2">
              <Text className="text-sm font-medium">Current Org Members</Text>
              <div className="flex flex-col gap-1">
                {pendingAddUserIds.map((userId) => {
                  const member = currentOrgMembers.find((m) => String(m.user.id) === userId);
                  if (!member) return null;
                  const name = getNonNullUserName(member.user.name);
                  const email = member.user.email ?? '';
                  return (
                    <div key={userId} className="flex items-center gap-2 w-full min-w-0">
                      <AvatarIcon user={{ name, email }} size="small" className="shrink-0" />
                      <div className="flex items-center gap-5 min-w-0 flex-1 truncate">
                        <Text className="text-xs text-grey-80 truncate">{name || email}</Text>
                        <Text className="text-xs text-grey-50 truncate">{email}</Text>
                      </div>
                      <Button
                        size="icon"
                        variant="hollow"
                        type="button"
                        aria-label={`Remove ${name || email}`}
                        onClick={() => handleUnstageAdd(userId)}
                        data-testid={`invite-team-project-unstage-${userId}`}
                      >
                        <SvgIcon icon="trash" size={14} />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {selectedExternalMembers.size > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {/* Header row */}
              <div className="col-span-1 flex items-end">
                <Text className="text-sm font-medium leading-4">Invite to CivilGrid</Text>
              </div>
              <div className="col-span-1 flex items-end gap-1">
                <AboutUserRoles
                  trigger={
                    <div className="flex items-center gap-1 cursor-pointer tab-index-0">
                      <Text className="text-xs font-medium leading-4">About Roles</Text>
                      <Icon icon="questionMarkCircled" />
                    </div>
                  }
                />
              </div>
              <div className="col-span-1" />
              {/* Data rows: email | role select | apply to all | trash */}
              {selectedExternalMembersArray.map((member, index) => (
                <React.Fragment key={member.email}>
                  <div className="col-span-1 flex items-center min-w-0">
                    <Text className="text-xs text-grey-80 truncate">{member.email}</Text>
                  </div>
                  <div className="col-span-1 flex flex-col gap-0.5 min-w-0">
                    <Select
                      options={roles}
                      value={member.role ?? ''}
                      onValueChange={(newRole) => newRole && orgMembersStore.updateMemberRole(member.email, newRole)}
                      placeholder="Select role..."
                      aria-label={`Role for ${member.email}`}
                      aria-invalid={!member.role}
                      triggerClassName="w-40"
                      data-testid={`invite-team-project-role-select-${member.email}`}
                    />
                  </div>
                  <div className="col-span-1 flex items-center justify-between">
                    {member.role && selectedExternalMembersArray.length > 1 && index === 0 ? (
                      <IconButton
                        icon="copy"
                        size="icon"
                        variant="hollow"
                        tooltip="Apply role to all invited members"
                        onClick={() => member.role && orgMembersStore.updateAllMembersRole(member.role)}
                      >
                        <Text className="text-xs text-grey-80">Apply to all</Text>
                      </IconButton>
                    ) : (
                      <div className="w-4" />
                    )}
                    <Button
                      size="icon"
                      variant="hollow"
                      type="button"
                      aria-label={`Remove ${member.email}`}
                      onClick={() => orgMembersStore.toggleExternalMember(member.email)}
                      data-testid={`invite-team-project-remove-${member.email}`}
                    >
                      <SvgIcon icon="trash" size={14} />
                    </Button>
                  </div>
                </React.Fragment>
              ))}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleInviteMembers} disabled={!hasAnythingToSubmit || controlledIsPending}>
            {controlledIsPending ? 'Inviting…' : 'Invite'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
